import React, { useState } from 'react';

interface ConnectWalletScreenProps {
  isConnecting: boolean;
  connectors: readonly any[];
  onConnect: (connector: any) => void;
  isSolanaConnecting?: boolean;
  onConnectSolana?: () => void;
  solanaAvailable?: boolean;
  isSuiConnecting?: boolean;
  onConnectSui?: (wallet?: any) => void;
  suiAvailable?: boolean;
  suiWallets?: any[];
}

export const ConnectWalletScreen: React.FC<ConnectWalletScreenProps> = ({
  isConnecting,
  connectors,
  onConnect,
  isSolanaConnecting = false,
  onConnectSolana,
  solanaAvailable = false,
  isSuiConnecting = false,
  onConnectSui,
  suiAvailable = false,
  suiWallets = [],
}) => {
  // Find injected and walletConnect connectors
  const injectedConnectors = connectors.filter(c => c.type === 'injected' || c.id === 'injected');
  const walletConnectConnector = connectors.find(c => c.id === 'walletConnect' || c.name === 'WalletConnect');
  const evmConnectors = walletConnectConnector
    ? [...injectedConnectors, walletConnectConnector]
    : injectedConnectors;
  const primaryEvmConnector = evmConnectors[0];
  const hasMultipleEvmConnectors = evmConnectors.length > 1;

  const solanaDisabled = !solanaAvailable || isConnecting || isSolanaConnecting;
  const suiDisabled = !suiAvailable || isConnecting || isSuiConnecting;
  const primarySuiWallet = suiWallets[0];
  const hasMultipleSuiWallets = suiWallets.length > 1;
  const [isEvmModalOpen, setIsEvmModalOpen] = useState(false);
  const [isSuiModalOpen, setIsSuiModalOpen] = useState(false);

  return (
    <div className="text-center py-6 sm:py-8 md:py-12 px-4">
      <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Connect Your Wallet</h2>
      <p className="text-sm  text-gray-400 mb-6 sm:mb-8 px-2">
        Choose how you want to connect your wallet
      </p>

      <div className="flex flex-col gap-3 sm:gap-4 items-stretch max-w-2xl mx-auto">
        {/* Browser Extension Button */}
        {primaryEvmConnector && (
          <button
            onClick={() => {
              if (hasMultipleEvmConnectors) {
                setIsEvmModalOpen(true);
              } else {
                onConnect(primaryEvmConnector);
              }
            }}
            disabled={isConnecting}
            className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-600 rounded-lg text-base sm:text-lg font-medium flex items-center justify-start gap-2 sm:gap-3 transition-colors min-h-[56px] sm:min-h-[64px] text-left"
          >
            {isConnecting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span className="text-sm ">Connecting...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div className="text-left">
                  <div className="font-semibold text-sm ">EVM Wallet</div>
                  <div className="text-xs sm:text-sm opacity-90">
                    {hasMultipleEvmConnectors
                      ? `Select provider (${evmConnectors.length} detected)`
                      : 'MetaMask, WalletConnect, etc.'}
                  </div>
                </div>
              </>
            )}
          </button>
        )}

        {/* Solana Wallet Button */}
        {onConnectSolana && (
          <button
            onClick={() => onConnectSolana()}
            disabled={solanaDisabled}
            className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-orange-600 hover:bg-orange-700 active:bg-orange-800 disabled:bg-gray-600 rounded-lg text-base sm:text-lg font-medium flex items-center justify-start gap-2 sm:gap-3 transition-colors min-h-[56px] sm:min-h-[64px] text-left"
          >
            {isSolanaConnecting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span className="text-sm ">Connecting...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <defs>
                    <linearGradient id="solana-gradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#14F195" />
                      <stop offset="100%" stopColor="#9945FF" />
                    </linearGradient>
                  </defs>
                  <path d="M6.2 6.3a.8.8 0 0 1 .6-.3h12.9c.6 0 .9.8.4 1.2l-2 1.9a.8.8 0 0 1-.6.2H4.6c-.6 0-.9-.8-.4-1.2l2-1.8z" fill="url(#solana-gradient)" />
                  <path d="M6.2 12.2a.8.8 0 0 1 .6-.2h12.9c.6 0 .9.7.4 1.1l-2 2a.8.8 0 0 1-.6.2H4.6c-.6 0-.9-.7-.4-1.1l2-2z" fill="url(#solana-gradient)" />
                  <path d="M6.2 18.1a.8.8 0 0 1 .6-.2h12.9c.6 0 .9.7.4 1.1l-2 2a.8.8 0 0 1-.6.2H4.6c-.6 0-.9-.7-.4-1.1l2-2z" fill="url(#solana-gradient)" />
                </svg>
                <div className="text-left">
                  <div className="font-semibold text-sm ">Solana Wallet</div>
                  <div className="text-xs sm:text-sm opacity-90">
                    {solanaAvailable ? 'Select provider (1 detected)' : 'Wallet not detected'}
                  </div>
                </div>
              </>
            )}
          </button>
        )}

        {/* Sui Wallet Button */}
        {onConnectSui && (
          <button
            onClick={() => {
              if (hasMultipleSuiWallets) {
                setIsSuiModalOpen(true);
              } else {
                onConnectSui(primarySuiWallet);
              }
            }}
            disabled={suiDisabled}
            className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:bg-gray-600 rounded-lg text-base sm:text-lg font-medium flex items-center justify-start gap-2 sm:gap-3 transition-colors min-h-[56px] sm:min-h-[64px] text-left"
          >
            {isSuiConnecting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span className="text-sm ">Connecting...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2.2c-3.9 4.2-6.4 8-6.4 11.2 0 4.1 2.9 7.6 6.4 8.8 3.5-1.2 6.4-4.7 6.4-8.8 0-3.2-2.5-7-6.4-11.2z" fill="#4DA2FF" />
                  <path d="M12 5.7c-2.4 3-3.8 5.6-3.8 7.7 0 2.6 1.6 4.8 3.8 5.8 2.2-1 3.8-3.2 3.8-5.8 0-2.1-1.4-4.7-3.8-7.7z" fill="#E6F2FF" />
                </svg>
                <div className="text-left">
                  <div className="font-semibold text-sm ">Sui Wallet</div>
                  <div className="text-xs sm:text-sm opacity-90">
                    {suiAvailable
                      ? hasMultipleSuiWallets
                        ? `Select provider (${suiWallets.length} detected)`
                        : 'Wallet Standard for SUI'
                      : 'Wallet not detected'}
                  </div>
                </div>
              </>
            )}
          </button>
        )}

        {isEvmModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setIsEvmModalOpen(false)}
          >
            <div
              className="bg-gray-800 rounded-lg p-5 sm:p-6 md:p-8 max-w-md w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Choose Wallet Extension</h3>
                  <p className="text-sm text-gray-300 mt-1">Select a provider to connect.</p>
                </div>
                <button
                  onClick={() => setIsEvmModalOpen(false)}
                  className="text-gray-300 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-3">
                {evmConnectors.map((connector, index) => (
                  <button
                    key={connector?.id || connector?.name || index}
                    onClick={() => {
                      setIsEvmModalOpen(false);
                      onConnect(connector);
                    }}
                    disabled={isConnecting}
                    className="w-full px-4 sm:px-5 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-600 rounded-lg text-base font-medium flex items-center justify-start gap-3 transition-colors text-left"
                  >
                    {connector?.icon ? (
                      <img src={connector.icon} alt={connector?.name || 'Wallet'} className="w-6 h-6 rounded-full" />
                    ) : (
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    )}
                    <div className="text-left">
                      <div className="font-semibold text-sm ">{connector?.name || 'Wallet Extension'}</div>
                      <div className="text-xs opacity-90">
                        {connector?.id === 'walletConnect' || connector?.name === 'WalletConnect'
                          ? 'WalletConnect'
                          : 'Browser extension'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {isSuiModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setIsSuiModalOpen(false)}
          >
            <div
              className="bg-gray-800 rounded-lg p-5 sm:p-6 md:p-8 max-w-md w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Choose Sui Wallet</h3>
                  <p className="text-sm text-gray-300 mt-1">Select a provider to connect.</p>
                </div>
                <button
                  onClick={() => setIsSuiModalOpen(false)}
                  className="text-gray-300 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-3">
                {suiWallets.map((wallet, index) => (
                  <button
                    key={wallet?.name || index}
                    onClick={() => {
                      setIsSuiModalOpen(false);
                      onConnectSui(wallet);
                    }}
                    disabled={suiDisabled}
                    className="w-full px-4 sm:px-5 py-3 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:bg-gray-600 rounded-lg text-base font-medium flex items-center justify-start gap-3 transition-colors text-left"
                  >
                    {wallet?.icon ? (
                      <img src={wallet.icon} alt={wallet?.name || 'Sui wallet'} className="w-6 h-6 rounded-full" />
                    ) : (
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l7 4v6c0 5-3.5 9-7 9s-7-4-7-9V7l7-4z" />
                      </svg>
                    )}
                    <div className="text-left">
                      <div className="font-semibold text-sm ">{wallet?.name || 'Sui Wallet'}</div>
                      <div className="text-xs opacity-90">Wallet Standard</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Fallback if no connectors found */}
        {!primaryEvmConnector && connectors.length > 0 && (
          <button
            onClick={() => onConnect(connectors[0])}
            disabled={isConnecting}
            className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-600 rounded-lg text-base sm:text-lg font-medium min-h-[56px] sm:min-h-[64px] transition-colors"
          >
            {isConnecting ? 'Connecting...' : 'Connect Wallet'}
          </button>
        )}
      </div>
    </div>
  );
};
